import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import platformAdminApi, { setPaCsrfToken } from '@/lib/platformAdminApi';
import type { ApiResponse } from '@amuster/types';

interface PlatformAdmin {
  id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  mfa_enabled: boolean;
}

interface PlatformAdminLoginResponse {
  admin: PlatformAdmin;
  expires_at: string;
}

interface PlatformAdminMeResponse {
  admin: PlatformAdmin;
}

interface PlatformAdminContextValue {
  admin: PlatformAdmin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAdmin: () => Promise<void>;
}

const PlatformAdminContext = createContext<PlatformAdminContextValue | undefined>(undefined);

/**
 * Fetch CSRF token for platform admin API.
 * Note: Platform admin may share CSRF with main auth or have separate endpoint.
 * For now, we use a simple GET request to establish the CSRF cookie.
 */
async function fetchPaCsrfToken(): Promise<void> {
  try {
    // Platform admin endpoints should set CSRF cookie on any authenticated request
    // Using the /auth/me endpoint to establish CSRF cookie
    const response = await platformAdminApi.get<{ success: boolean; data: { csrf_token?: string } }>('/auth/csrf-token');
    if (response.data.data?.csrf_token) {
      setPaCsrfToken(response.data.data.csrf_token);
    }
  } catch {
    // CSRF fetch may fail if not authenticated; that's OK
  }
}

export function PlatformAdminProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<PlatformAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Bootstrap platform admin session from server.
   * Cookie-first: server reads access_token from HttpOnly cookie.
   */
  const fetchMe = useCallback(async () => {
    try {
      const response = await platformAdminApi.get<ApiResponse<PlatformAdminMeResponse>>('/auth/me');
      setAdmin(response.data.data.admin);
    } catch {
      // Not authenticated
      setAdmin(null);
    }
  }, []);

  // Bootstrap on mount
  useEffect(() => {
    const bootstrap = async () => {
      await fetchPaCsrfToken();
      await fetchMe();
      setIsLoading(false);
    };
    bootstrap();
  }, [fetchMe]);

  /**
   * Login with email/password.
   * API sets HttpOnly cookies; we update local state from response.
   */
  const login = useCallback(async (email: string, password: string) => {
    const response = await platformAdminApi.post<ApiResponse<PlatformAdminLoginResponse>>(
      '/auth/login',
      { email, password }
    );

    const { admin: adminData } = response.data.data;
    setAdmin(adminData);

    // Refresh CSRF token after login
    await fetchPaCsrfToken();
  }, []);

  /**
   * Logout and clear session.
   * API clears HttpOnly cookies.
   */
  const logout = useCallback(async () => {
    try {
      await platformAdminApi.post('/auth/logout');
    } catch {
      // Ignore logout errors
    } finally {
      setAdmin(null);
    }
  }, []);

  /**
   * Refresh admin data from server.
   */
  const refreshAdmin = useCallback(async () => {
    await fetchMe();
  }, [fetchMe]);

  return (
    <PlatformAdminContext.Provider
      value={{
        admin,
        isAuthenticated: !!admin,
        isLoading,
        login,
        logout,
        refreshAdmin,
      }}
    >
      {children}
    </PlatformAdminContext.Provider>
  );
}

export function usePlatformAdmin() {
  const context = useContext(PlatformAdminContext);
  if (!context) {
    throw new Error('usePlatformAdmin must be used within a PlatformAdminProvider');
  }
  return context;
}
